import {ToolName, ToolResultPayloadType} from '../../copilot.enums';
import {ToolExecuteParams, ToolResult} from '../../copilot.types';
import tool from '../../utils/creators/tool';
import toolResult from '../../utils/creators/toolResult';
import {logger} from '../../utils/logger';
import {resourceKnowledgeConfigs} from './resourceKnowledgeTool.config';
import {resourceKnowledgeToolInputSchema} from './resourceKnowledgeTool.schemas';
import askAboutResources from './utils/askAboutResources';
import buildResources from './utils/buildResources';

function resourceKnowledgeTool() {
  async function execute({
    input: {resourceType, resourcesToFetch, prompt, isQuestionTooBroad},
    abortSignal,
  }: ToolExecuteParams<typeof resourceKnowledgeToolInputSchema>): Promise<ToolResult> {
    const {formatter, additionalInstructions, linkBuilder, searchFunction} =
      resourceKnowledgeConfigs[resourceType];

    try {
      if (isQuestionTooBroad) {
        return {
          content: `Tell the user that they need to ask a more specific question, or to refer to the link below to see all ${resourceType}s. The link will be provided below your response - you don't need to provide it in your answer.`,
          payload: {
            type: ToolResultPayloadType.Resource,
            resources: buildResources([], linkBuilder),
          },
        };
      }

      const {resources: fcResources, filtersExplanation} = await searchFunction(resourcesToFetch);

      // add a temp ID per resource. This allows "askAboutResources" to point to which resources are relevant to the user's question
      const fcResourcesWithTempIds = fcResources.map((fcResource, index) => ({
        resource: fcResource,
        tempId: `temp-id-${index + 1}`,
      }));

      logger.log(`${resourceType} resources found`, {fcResourcesWithTempIds, filtersExplanation});

      if (fcResources.length === 0) {
        return {
          content: `No matching ${resourceType} resources found`,
          payload: {
            type: ToolResultPayloadType.Resource,
            resources: buildResources([], linkBuilder),
          },
        };
      }

      const {relevantTempIds, answer} = await askAboutResources({
        resourcesWithTempIds: fcResourcesWithTempIds,
        formatter,
        prompt,
        additionalInstructions,
        abortSignal,
      });

      const relevantFcResources = fcResourcesWithTempIds
        .filter((resource) => relevantTempIds.includes(resource.tempId))
        .map((resourceWithTempId) => resourceWithTempId.resource);

      const relevantResources = buildResources(relevantFcResources, linkBuilder);

      return {
        content: JSON.stringify({answer, additionalInstructions}),
        payload: {
          type: ToolResultPayloadType.Resource,
          resources: relevantResources,
        },
      };
    } catch (error) {
      return toolResult(`Error searching for ${resourceType} resources: ${error.message}`);
    }
  }

  const resourceTypesDescriptions = Object.values(resourceKnowledgeConfigs)
    .map((config) => config.description)
    .join(', ');

  return tool({
    name: ToolName.ResourceKnowledge,
    displayName: 'Searching resources',
    description: `Search for answers about resources (${resourceTypesDescriptions}).`,
    execute,
    inputSchema: resourceKnowledgeToolInputSchema,
  });
}

export default resourceKnowledgeTool;
